/**
 * Les mots de l'impact d'une absence du masteur, élève par élève — portés tels quels de l'ancien
 * `tutor/lives/absenceImpact.js`. Le backend décide (`GET live-sessions/{id}/absence-impact`, règle
 * `TutorAbsenceRelationRule`) ; l'app ne rejuge rien, elle met des mots dessus. On dit « masteur »,
 * et rien ne s'accorde au genre de qui lit : les phrases passent par « ton absence ».
 *
 * Pur, à côté de la molécule qui le pose (comme `absenceQuotaMessage`).
 */
import { type UITutorAbsenceImpactStudent } from "../../../ports/primary/ui/types/tutorLives"
import { joinFrench } from "../../../ports/primary/ui/formatters/textFormatter"

export const IMPACT_TITLE = "Ce que ton absence change"

const FINAL = "C'est définitif, même si tu annules ton absence."

/** La phrase de fin du bloc, qui n'existe que si un élève change de masteur. */
export const TUTOR_CHANGE_CONSEQUENCE =
    "Un élève qui débute avec toi a besoin de son masteur dès le début : " +
    `ton absence lui en donne un nouveau. ${FINAL}`

export const isTutorChange = (student: UITutorAbsenceImpactStudent) => student.outcome === "tutor_change"

const tutorChanges = (students: UITutorAbsenceImpactStudent[] | null | undefined) =>
    (students ?? []).filter(isTutorChange)

/** Sans changement de masteur, l'écran de confirmation reste celui d'avant : pas de bloc. */
export const hasTutorChange = (students: UITutorAbsenceImpactStudent[] | null | undefined) =>
    tutorChanges(students).length > 0

export const impactStatusLabel = (student: UITutorAbsenceImpactStudent) =>
    isTutorChange(student) ? "Change de masteur" : "Remplacé"

/** Sous le prénom : pourquoi il change de masteur, et, à moins de 24 h, la séance qu'il garde ; pour un remplacé, qui assure. */
export function impactDetails(student: UITutorAbsenceImpactStudent): string[] {
    if (!isTutorChange(student)) return ["Un autre masteur assure cette séance."]
    const reason =
        student.reason === "relation_not_started"
            ? "Vous n'avez pas encore eu cours ensemble."
            : student.reason === "nascent_relation_long_absence"
              ? `Vous venez de commencer, et ton absence touche ${student.touchedOccurrences ?? 0} de vos séances.`
              : null
    return [
        reason,
        student.shortNotice === true ? "Cette séance est assurée par un remplaçant, puis changement de masteur." : null,
    ].filter((line): line is string => line != null)
}

/** Tous changent de masteur, aucun ne garde la séance : elle se vide — aucun crédit d'absence n'est décompté. */
export const emptiesSession = (students: UITutorAbsenceImpactStudent[] | null | undefined) =>
    (students ?? []).length > 0 &&
    (students ?? []).every((student) => isTutorChange(student) && student.shortNotice !== true)

/** La boîte du refus, loin du bloc : elle nomme elle-même les élèves qui changent de masteur. */
export function tutorChangeNotice(students: UITutorAbsenceImpactStudent[]): string {
    const names = tutorChanges(students).map((student) => student.firstname)
    return (
        `Avec ton absence, ${joinFrench(names)} ${names.length > 1 ? "changent" : "change"} de masteur. ` +
        `${FINAL} Confirme à nouveau pour déclarer ton absence.`
    )
}
